import "@/App.css";
import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";

import { AuthProvider } from "@/context/AuthContext";
import { ActiveContestProvider } from "@/context/ActiveContestContext";
import { ConfigProvider } from "@/context/ConfigContext";
import { StripeConfigProvider } from "@/context/StripeConfigContext";
import { ThemeProvider } from "@/theme/ThemeProvider";
import ProtectedRoute from "@/components/ProtectedRoute";
import AdminModeBar from "@/components/AdminModeBar";
import AuthedRedirect from "@/components/AuthedRedirect";
import RouteViewSync from "@/components/RouteViewSync";
import MetaPixelPageView from "@/components/MetaPixelPageView";
import AnalyticsPageView from "@/components/AnalyticsPageView";
import ShareArrivalTracker from "@/components/ShareArrivalTracker";
import ConsentBanner from "@/components/ConsentBanner";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import GoogleAuthCallback from "@/pages/GoogleAuthCallback";
import CustomerShell from "@/components/customer/CustomerShell";
import CustomerDashboard from "@/pages/CustomerDashboard";
import CustomerBookingsPage from "@/pages/customer/CustomerBookingsPage";
import CustomerDogsPage from "@/pages/customer/CustomerDogsPage";
import CustomerBillingPage from "@/pages/customer/CustomerBillingPage";
import CustomerSettingsPage from "@/pages/customer/CustomerSettingsPage";
import ProviderShell from "@/components/provider/ProviderShell";
import ProviderSchedulePage from "@/pages/provider/ProviderSchedulePage";
import ProviderEarningsPage from "@/pages/provider/ProviderEarningsPage";
import ProviderPluckonomicsPage from "@/pages/provider/ProviderPluckonomicsPage";
import ProviderReviewsPage from "@/pages/provider/ProviderReviewsPage";
import ProviderCoveragePage from "@/pages/provider/ProviderCoveragePage";
import ProviderProfilePage from "@/pages/provider/ProviderProfilePage";
import ProviderSettingsPage from "@/pages/provider/ProviderSettingsPage";
import ProviderConnectReturn from "@/pages/ProviderConnectReturn";
import BookingWizard from "@/pages/BookingWizard";
import BookingConfirmation from "@/pages/BookingConfirmation";
import BookingSuccess from "@/pages/BookingSuccess";
import BookingCancel from "@/pages/BookingCancel";
import ReviewSubmitPage from "@/pages/ReviewSubmitPage";
// PartnerApplyPage removed — Prod Hotfix Batch 1 P0-2 (Disposition A).
// The legacy gig-era intake form is retired; `/partner/apply` now
// redirects to `/signup?role=provider`. See ADR in App.js route
// block below.
import PartnerTiersPage from "@/pages/PartnerTiersPage";
import PartnerOnboardingPage from "@/pages/PartnerOnboardingPage";
import PartnerServiceAreaPage from "@/pages/PartnerServiceAreaPage";
import PartnerPricingWizardPage from "@/pages/PartnerPricingWizardPage";
import PartnerTierUpgradePage from "@/pages/PartnerTierUpgradePage";
import BusinessIntakePage from "@/pages/partner/BusinessIntakePage";
import IntakeStatusPage from "@/pages/partner/IntakeStatusPage";
import AdminPoopartnerIntakesPage from "@/pages/admin/AdminPoopartnerIntakesPage";
// Admin-Audit-Browser dispatch — read-only browser over `audit_events`.
import AdminAuditEventsPage from "@/pages/admin/AdminAuditEventsPage";
import {
  LegalTermsPage,
  LegalPartnerAgreementPage,
  LegalPrivacyPage,
} from "@/pages/legal/LegalPages";
import GiftPurchasePage from "@/pages/GiftPurchasePage";
import GiftRedeemPage from "@/pages/GiftRedeemPage";
import AdminPortal from "@/pages/AdminPortal";
import {
  AccountsAdminPage,
  PooBucksAdminPage,
  FeesAdminPage,
  TiersAndPlansAdminPage,
  SettingsAdminPage,
  DashboardAdminPage,
  BookingsAdminPage,
  AnalyticsAdminPage,
  PooPartnersAdminPage,
} from "@/pages/admin/AdminSectionPages";
import AdminReviewsPage from "@/pages/admin/AdminReviewsPage";
import AdminServiceAreasPage from "@/pages/admin/AdminServiceAreasPage";
import AdminPartnerVerificationsPage from "@/pages/admin/AdminPartnerVerificationsPage";
import AdminPromoCodesPage from "@/pages/admin/AdminPromoCodesPage";
import AdminPayoutAccountsPage from "@/pages/admin/AdminPayoutAccountsPage";
import AdminPartnerServiceAreasPage from "@/pages/admin/AdminPartnerServiceAreasPage";
import AdminTierDemandFunnel from "@/pages/admin/AdminTierDemandFunnel";
import AdminLaunchReadinessDashboard from "@/pages/admin/AdminLaunchReadinessDashboard";
import AdminAnalyticsTrafficPage from "@/pages/admin/AdminAnalyticsTrafficPage";
import AcceptAdminInvite from "@/pages/AcceptAdminInvite";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import NotFoundPage from "@/pages/NotFoundPage";
import ContestEnterPage from "@/pages/contest/ContestEnterPage";
import CutestPluckLandingPage from "@/pages/contest/CutestPluckLandingPage";
import CutestPluckEntryPage from "@/pages/contest/CutestPluckEntryPage";
import CutestPluckRulesPage from "@/pages/contest/CutestPluckRulesPage";
import AdminContestEntriesPage from "@/pages/admin/AdminContestEntriesPage";
import AdminContestConfigPage from "@/pages/admin/AdminContestConfigPage";
import AdminContestRankingPage from "@/pages/admin/AdminContestRankingPage";
import AdminContestArchivePage from "@/pages/admin/AdminContestArchivePage";
import AdminWinnerSlotsPage from "@/pages/admin/AdminWinnerSlotsPage";
import AdminWaitlistPage from "@/pages/admin/AdminWaitlistPage";
import About from "@/pages/About";
import Pricing from "@/pages/Pricing";
import Faq from "@/pages/Faq";
import MapPage from "@/pages/Map";
import SocialPage from "@/pages/Social";
import JustPluckingAroundPage from "@/pages/JustPluckingAround";
import CommunityPage from "@/pages/Community";
import WaitlistPage from "@/pages/Waitlist";
import PartnersPage from "@/pages/Partners";
import Terms from "@/pages/Terms";
import Privacy from "@/pages/Privacy";
import Contact from "@/pages/Contact";
import PublicPageRouter from "@/components/PublicPageRouter";
import AppErrorBoundary from "@/components/AppErrorBoundary";

/**
 * AppShell — route surface for the entire app.
 *
 * Phase 1 of the App Shell Redesign reshaped the admin route tree:
 *   - `/admin` is now a layout route (`<AdminPortal>` renders the shell
 *     w/ an `<Outlet>`); each section lives at its own URL.
 *   - `<AuthedRedirect>` wraps `/`, `/login`, `/signup` so logged-in
 *     users never see the public marketing surface.
 *   - `<RouteViewSync>` keeps the active workspace in lockstep with
 *     the URL (URL is the source of truth for multi-role users).
 *   - `AdminModeBar` is now gated on `is_impersonating` (Backlog D) so
 *     plain admin sessions get the slate sidebar as the only admin
 *     indicator. Until D ships the flag, the bar effectively never
 *     renders — and the legacy `pt-9` shell-padding hack is dropped.
 */
function AppShell() {
  return (
    <>
      <AdminModeBar />
      <RouteViewSync />
      <MetaPixelPageView />
      <AnalyticsPageView />
      <ShareArrivalTracker />
      {/* Feb 2026 Lane E — consent banner. Renders ONLY when
          REACT_APP_CONSENT_GATING_ENABLED === "true" AND the user
          has not yet chosen. Ships DARK — flag is absent from
          `.env` by default, so this component is a no-op path on
          today's launch. See `lib/consent.js` module header. */}
      <ConsentBanner />
      <div data-testid="app-shell">
        <Routes>
          {/* Public surfaces — but redirect logged-in users to their dashboard. */}
          <Route path="/" element={<AuthedRedirect><Landing /></AuthedRedirect>} />
          <Route path="/login" element={<AuthedRedirect><Login /></AuthedRedirect>} />
          <Route path="/signup" element={<AuthedRedirect><Signup /></AuthedRedirect>} />
          <Route path="/partners" element={<PartnersPage />} />
          <Route path="/waitlist" element={<PublicPageRouter><WaitlistPage /></PublicPageRouter>} />
          <Route path="/forgot-password" element={<AuthedRedirect><ForgotPasswordPage /></AuthedRedirect>} />
          <Route path="/reset-password" element={<AuthedRedirect><ResetPasswordPage /></AuthedRedirect>} />

          {/* Cutest Pluck Contest — Phase A. Entry submission is
              behind ProtectedRoute so unauthed users land on
              /login?next=/contest/enter, then bounce back here after
              a successful login. Any authed role can enter. */}
          <Route
            path="/contest/enter"
            element={
              <ProtectedRoute>
                <ContestEnterPage />
              </ProtectedRoute>
            }
          />

          {/* Cutest Pluck Contest — Phase B. Public contest hub +
              per-entry detail. No auth wall (the Vote button in-page
              redirects to /login?next=... when the caller is unauth). */}
          <Route path="/cutest-pluck" element={<CutestPluckLandingPage />} />
          <Route
            path="/cutest-pluck/entries/:entryId"
            element={<CutestPluckEntryPage />}
          />
          {/* Cutest Pluck Contest — Rules Page (Item A, 2026-07-23).
              Static long-form rules doc reading dynamic contest data
              (dates / prize / winner_count) from `ActiveContestContext`.
              Public route — no auth required. */}
          <Route
            path="/cutest-pluck/rules"
            element={<CutestPluckRulesPage />}
          />
          <Route path="/auth/google" element={<GoogleAuthCallback />} />
          <Route path="/admin-invite/accept" element={<AcceptAdminInvite />} />
          <Route path="/about"   element={<PublicPageRouter><About /></PublicPageRouter>} />
          <Route path="/pricing" element={<PublicPageRouter><Pricing /></PublicPageRouter>} />
          <Route path="/faq"     element={<PublicPageRouter><Faq /></PublicPageRouter>} />
          <Route path="/map"     element={<PublicPageRouter><MapPage /></PublicPageRouter>} />
          <Route path="/social"  element={<PublicPageRouter><SocialPage /></PublicPageRouter>} />
          <Route
            path="/social/just-plucking-around"
            element={<PublicPageRouter><JustPluckingAroundPage /></PublicPageRouter>}
          />
          <Route
            path="/social/community"
            element={<PublicPageRouter><CommunityPage /></PublicPageRouter>}
          />
          <Route path="/terms"   element={<PublicPageRouter><Terms /></PublicPageRouter>} />
          <Route path="/privacy" element={<PublicPageRouter><Privacy /></PublicPageRouter>} />
          <Route path="/contact" element={<PublicPageRouter><Contact /></PublicPageRouter>} />

          {/* PooPartner Intake Revision — Phase 1c. Three placeholder
              legal pages, referenced from the intake wizard's
              legal-agreement checkbox links. Real counsel-reviewed
              copy replaces the placeholder text without changing the
              route paths; the legal-version-id bump (backend) is the
              paired dispatch when that happens. */}
          <Route
            path="/legal/terms"
            element={<PublicPageRouter><LegalTermsPage /></PublicPageRouter>}
          />
          <Route
            path="/legal/partner-agreement"
            element={<PublicPageRouter><LegalPartnerAgreementPage /></PublicPageRouter>}
          />
          <Route
            path="/legal/privacy"
            element={<PublicPageRouter><LegalPrivacyPage /></PublicPageRouter>}
          />

          {/* Prod Hotfix Batch 1 — P0-2 (Disposition A). `/partner/apply`
              previously served the legacy gig-era intake form
              ("Make extra dough while making dogs happy" + 1099
              checkbox). That copy contradicted the Phase 1c
              business-only B2B intake wizard at
              `/partner/business-intake` and was a brand liability on
              a public URL. The route is preserved as a REDIRECT so
              external links / bookmarks still resolve — target is
              `/signup?role=provider`, the shared pre-auth funnel
              already wired from Landing / Footer / Map / About / FAQ /
              Pricing. Signed-up providers land on the wizard via
              post-signup routing. Drift-guard: `PartnerApply.copyAudit.test.jsx`.
              The legacy component + tests are deleted. */}
          <Route
            path="/partner/apply"
            element={<Navigate to="/signup?role=provider" replace />}
          />
          <Route path="/partner/tiers" element={<PublicPageRouter><PartnerTiersPage /></PublicPageRouter>} />

          {/* PooPartner Intake Revision — Phase 1c business-only
              wizard. AUTH-gated. Orthogonal to the pre-auth
              `/partner/apply` lead-capture (which stays as the
              marketing front door) and to `/partner/onboarding`
              (Stripe Identity funnel — Phase H). Provider capability
              is not enforced at the route level because a customer
              wanting to also become a partner should be able to
              reach the wizard; the backend does not currently
              require provider role for intake submit. */}
          <Route
            path="/partner/business-intake"
            element={
              <ProtectedRoute>
                <BusinessIntakePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/partner/intake/status"
            element={
              <ProtectedRoute>
                <IntakeStatusPage />
              </ProtectedRoute>
            }
          />

          {/* Phase H — Partner identity verification funnel. Gated to
              provider capability (multi-role admins keep access if
              they also carry is_provider). Reflects the 7 states of
              the `partner_verifications` state machine. */}
          <Route
            path="/partner/onboarding"
            element={
              <ProtectedRoute role="provider">
                <PartnerOnboardingPage />
              </ProtectedRoute>
            }
          />
          {/* Phase J — Service-area configuration. Server enforces the
              same verification.approved + payouts_enabled gate that
              /partner/onboarding walks partners through; the FE gate
              here is purely role-based (partner) so the page can
              render a helpful redirect message if the server 403s. */}
          <Route
            path="/partner/service-area"
            element={
              <ProtectedRoute role="provider">
                <PartnerServiceAreaPage />
              </ProtectedRoute>
            }
          />
          {/* Phase K — Final onboarding step: per-dog pricing + tier +
              subscription intent. Server enforces the full three-gate
              (verification.approved + payouts_enabled + service-area
              confirmed) before the wizard's confirm endpoint accepts. */}
          <Route
            path="/partner/pricing-wizard"
            element={
              <ProtectedRoute role="provider">
                <PartnerPricingWizardPage />
              </ProtectedRoute>
            }
          />
          {/* Phase K.3.3(b) — Partner-side tier upgrade path.
              Intent-only; admin promote-stub remains the sole
              activation path until Phase 3.8 flips real Stripe
              subscription billing on. */}
          <Route
            path="/partner/tier-upgrade"
            element={
              <ProtectedRoute role="provider">
                <PartnerTierUpgradePage />
              </ProtectedRoute>
            }
          />

          {/* Gift a PooPluck — public purchase (auth optional; guest
              checkout allowed) + auth-required redemption. Both routes
              wired here so the marketing/landing surface + email/SMS
              share-outs can deep-link without going through a shell. */}
          <Route path="/gift/buy" element={<GiftPurchasePage />} />
          <Route path="/gift/redeem" element={<GiftRedeemPage />} />

          {/* Admin route tree — layout w/ Outlet. Bare /admin redirects to
              the first BUILT section (Accounts) per Phase 1 default-landing
              decision; clicking the Dashboard sidebar item still shows the
              honest "Coming soon" stub. */}
          <Route
            path="/admin"
            element={
              <ProtectedRoute requireAdmin>
                <AdminPortal />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="/admin/dashboard" replace />} />
            <Route path="dashboard" element={<DashboardAdminPage />} />
            <Route path="bookings" element={<BookingsAdminPage />} />
            <Route path="analytics" element={<AnalyticsAdminPage />} />
            <Route path="analytics/traffic" element={<AdminAnalyticsTrafficPage />} />
            <Route path="reviews" element={<AdminReviewsPage />} />
            <Route path="service-areas" element={<AdminServiceAreasPage />} />
            <Route path="partner-verifications" element={<AdminPartnerVerificationsPage />} />
            <Route path="payout-accounts" element={<AdminPayoutAccountsPage />} />
            <Route path="partner-service-areas" element={<AdminPartnerServiceAreasPage />} />
            <Route path="tier-demand" element={<AdminTierDemandFunnel />} />
            <Route path="launch-readiness" element={<AdminLaunchReadinessDashboard />} />
            <Route path="contest/entries" element={<AdminContestEntriesPage />} />
            <Route path="contest/config" element={<AdminContestConfigPage />} />
            <Route path="contest/ranking" element={<AdminContestRankingPage />} />
            <Route path="contest/archive" element={<AdminContestArchivePage />} />
            <Route path="contest/slots" element={<AdminWinnerSlotsPage />} />
            {/* Pre-Launch Demand Capture — waitlist admin surface. */}
            <Route path="waitlist" element={<AdminWaitlistPage />} />
            {/* Bare `/admin/contest` → dates editor. Sidebar entry +
                bookmarked stem URL both rely on this redirect so an
                admin never lands on a 404. */}
            <Route path="contest" element={<Navigate to="/admin/contest/config" replace />} />
            <Route path="accounts" element={<AccountsAdminPage />} />
            <Route path="poopartners" element={<PooPartnersAdminPage />} />
            <Route path="poopartner-intakes" element={<AdminPoopartnerIntakesPage />} />
            {/* Admin-Audit-Browser dispatch — cross-cutting audit_events browser. */}
            <Route path="audit-events" element={<AdminAuditEventsPage />} />
            <Route path="poobucks" element={<PooBucksAdminPage />} />
            <Route path="tiers-and-plans" element={<TiersAndPlansAdminPage />} />
            <Route path="fees" element={<FeesAdminPage />} />
            <Route path="promo-codes" element={<AdminPromoCodesPage />} />
            <Route path="settings" element={<SettingsAdminPage />} />
          </Route>

          {/* Customer route tree — Phase 2 CustomerShell wraps the
              overview + sub-routes (My Plucks, My dogs, Billing). The
              shell renders an Outlet so each route's content drops in
              under the shared top-nav + Book a Pluck CTA + avatar. */}
          <Route
            path="/dashboard/customer"
            element={
              <ProtectedRoute role="customer">
                <CustomerShell />
              </ProtectedRoute>
            }
          >
            <Route index element={<CustomerDashboard />} />
            <Route path="bookings" element={<CustomerBookingsPage />} />
            <Route path="dogs" element={<CustomerDogsPage />} />
            <Route path="billing" element={<CustomerBillingPage />} />
            <Route path="settings" element={<CustomerSettingsPage />} />
          </Route>
          <Route
            path="/dashboard/provider"
            element={
              <ProtectedRoute role="provider">
                <ProviderShell />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="schedule" replace />} />
            <Route path="schedule" element={<ProviderSchedulePage />} />
            <Route path="earnings" element={<ProviderEarningsPage />} />
            <Route path="pluckonomics" element={<ProviderPluckonomicsPage />} />
            <Route path="reviews" element={<ProviderReviewsPage />} />
            <Route path="coverage" element={<ProviderCoveragePage />} />
            <Route path="profile" element={<ProviderProfilePage />} />
            <Route path="settings" element={<ProviderSettingsPage />} />
          </Route>
          <Route
            path="/provider/connect/return"
            element={
              <ProtectedRoute role="provider">
                <ProviderConnectReturn />
              </ProtectedRoute>
            }
          />
          {/* Booking-wizard family is wrapped in CustomerShell so the
              top-nav + Book a Pluck CTA + avatar dropdown stay present
              across the booking flow (Phase 2 dispatch — booking
              experience lives inside the customer shell). */}
          <Route
            element={
              <ProtectedRoute role="customer">
                <CustomerShell />
              </ProtectedRoute>
            }
          >
            <Route path="/book" element={<BookingWizard />} />
            <Route path="/book/confirmation/:id" element={<BookingConfirmation />} />
            <Route path="/booking/success" element={<BookingSuccess />} />
            <Route path="/booking/cancel" element={<BookingCancel />} />
            {/* Backlog B.2.x — auth-gated review deep-link. Lives
                inside the customer ProtectedRoute group so
                unauthenticated hits bounce to /login?next=… and
                land back here after login. No token in URL. */}
            <Route path="/review/:bookingId" element={<ReviewSubmitPage />} />
          </Route>
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </div>
    </>
  );
}

export default function App() {
  return (
    <div className="App">
      <ThemeProvider>
        <AuthProvider>
          <ConfigProvider>
            <StripeConfigProvider>
              <ActiveContestProvider>
                <BrowserRouter>
                  {/* Top-level error backstop. Mounted INSIDE the providers
                      so the fallback can use them if desired, but OUTSIDE
                      AppShell so a render-time throw from any route
                      component, sidebar, or shell-level provider produces
                      the branded fallback instead of unmounting #root.
                      Added 2026-06-29 in response to the missing-import
                      white-screen incident. */}
                  <AppErrorBoundary>
                    <AppShell />
                  </AppErrorBoundary>
                </BrowserRouter>
                <Toaster position="top-right" richColors />
              </ActiveContestProvider>
            </StripeConfigProvider>
          </ConfigProvider>
        </AuthProvider>
      </ThemeProvider>
    </div>
  );
}
